import { resolveDesignProfile } from "../design-profiles";
import { computeCableDemandAnalysis } from "../cable-demands";
import { effectiveTerminals } from "../effective";
import { detectMissingGround } from "./grounding";
export { detectMissingGround } from "./grounding";
import { detectUnsupportedBatteryIsolator } from "./isolators";
import { controlDesignSystems } from "./control-design-states";
import { dcChargingPortGroups, isDirectDcSource, pvInputTerminalGroups } from "../component-ports";
import { detectIncompletePvInput } from "./pv-input";
import { detectPvStringFuseExceedsModule } from "./pv-protection";
import { isAcCable, isAcTerminalId } from "../ac";
import { isFuseBlockLike, isFuseHolderVariant } from "../auto-fuse";
import { batteryConductorRegionIndex, computeBatterySourceRanges, computeBatteryStacks, type BatteryStack } from "../battery-stack";
import { getEffectiveCableFuse, isOverloaded } from "../cable-current";
import { settingsBySemantic, type DefLookup } from "../catalog";
import { dcPortTerminalGroups } from "../component-ports";
import { resolveTerminalPotentials } from "../terminal-voltage";
import { computeCrankingCables } from "../cranking-circuit";
import { diagnosticLabel } from "../i18n/diagnostic-label";
import { indexNodesById } from "../node-tree";
import { DEFAULT_AUTO_SIZE_VDROP_TARGET } from "../settings/auto-size-vdrop-default";
import { findCable, findNode } from "../system-lookup";
import { terminalPolarity } from "../terminal-snap";
import { makeDiagnosticCableSpecGetter } from "./sizing";
import type {
  ComponentDefinition,
  ComponentNode,
  Issue,
  System,
  Terminal,
} from "../types";
import {
  detectBatteryChemistryUnknown,
  detectFuseAicInadequate,
  detectLynxFuseSumExceedsRating,
  detectFuseRatingUnavailable,
  detectUntypedFuseOnHighFaultBank,
} from "./protection";
import {
  buildGraph,
  computeActiveSourceCircuits,
  computeActiveSolarPaths,
  isDcSideSupplied,
  findTerminals,
  isBatteryNegative,
  isBatteryPositive,
  isInverterDcInputPositive,
  isLoadNegative,
  isLoadPositive,
  multiSourceBfs,
  pathCables,
  reachable,
  splitV,
  v,
  type Vertex,
  type Graph,
} from "./graph";

const labelOf = (
  system: System,
  defLookup: DefLookup,
  nodeId: string,
): string => {
  const node = findNode(system, nodeId);
  if (!node) return nodeId;
  if (node.kind === "zone") return node.label ?? diagnosticLabel("diag.label.zone");
  const def = defLookup(node.definitionId, node.customDefinition);
  if (def?.renderVariant === "junction") return diagnosticLabel("diag.label.junction");
  return node.label ?? def?.shortName ?? def?.name ?? diagnosticLabel("diag.label.component");
};

/** A load whose positive reaches a battery+ without crossing a fused link. */
export function detectUnfusedRuns(
  system: System,
  defLookup: DefLookup,
  autoSizeTargetPct: number = DEFAULT_AUTO_SIZE_VDROP_TARGET,
): Issue[] {
  const issues = new Map<string, Issue>();
  const alternatives = controlDesignSystems(system, defLookup);
  for (const scenario of [system, ...alternatives]) {
    for (const issue of detectUnfusedRunsInState(scenario, defLookup, autoSizeTargetPct)) {
      // A single position does not establish the shared feed's maximum demand.
      // Keep the warning, but do not offer a fuse sized from just one throw.
      if (alternatives.length) delete issue.fixAction;
      const key = `${issue.code}:${issue.nodeIds?.join("|")}`;
      const previous = issues.get(key);
      if (previous) previous.cableIds = [...new Set([...(previous.cableIds ?? []), ...(issue.cableIds ?? [])])];
      else issues.set(key, issue);
    }
  }
  return [...issues.values()];
}

function detectUnfusedRunsInState(
  system: System,
  defLookup: DefLookup,
  autoSizeTargetPct: number,
): Issue[] {
  const profile = resolveDesignProfile(system);
  const hasBatteryPlacementRule = profile.status === "available" && profile.policy.protection.batteryDistanceM != null;
  const fusedGraph = buildGraph(system, defLookup);
  const unfusedGraph = buildGraph(system, defLookup, { excludeFused: true });

  const batteryPositives = findTerminals(system, defLookup, isBatteryPositive);
  if (batteryPositives.length === 0) return [];

  const unfusedReach = new Set<Vertex>();
  for (const seed of batteryPositives) {
    for (const v of reachable(unfusedGraph, seed)) unfusedReach.add(v);
  }
  const anyReach = new Set<Vertex>();
  for (const seed of batteryPositives) {
    for (const v of reachable(fusedGraph, seed)) anyReach.add(v);
  }

  // An inverting MultiPlus draws from the bank like a load; the catalog calls it a charger.
  const loadPositives = [
    ...findTerminals(system, defLookup, isLoadPositive),
    ...findTerminals(system, defLookup, isInverterDcInputPositive),
  ];
  const issues: Issue[] = [];
  const seen = new Set<string>();
  const specOf = makeDiagnosticCableSpecGetter(system, defLookup, autoSizeTargetPct);
  for (const lv of loadPositives) {
    if (!anyReach.has(lv)) continue;
    if (!unfusedReach.has(lv)) continue;
    const { nodeId } = splitV(lv);
    if (seen.has(nodeId)) continue;
    seen.add(nodeId);
    const label = labelOf(system, defLookup, nodeId);
    const { prev } = multiSourceBfs(unfusedGraph, batteryPositives);
    const cableIds = pathCables(unfusedGraph, lv, prev);
    // `pathCables` walks load → battery, so the last cable is the one at the
    // battery — where ABYC E-11 wants the fuse.
    const sourceCableId = cableIds[cableIds.length - 1];
    const sourceCable = sourceCableId ? findCable(system, sourceCableId) : undefined;
    let fixAction: Issue["fixAction"];
    // `spec.fuse === 0` is "nothing to rate against": the issue still stands,
    // but a one-click "add a 0 A fuse" is not a fix.
    if (sourceCable) {
      const spec = specOf(sourceCable);
      if (spec.fuse > 0 && spec.currentBasis !== "provisional-fallback" && !spec.sizingVerificationRequired && !spec.fuseRecommendationUnavailableReason) {
        fixAction = {
          kind: "set-cable-fuse",
          cableId: sourceCable.id,
          rating: spec.fuse,
          label: { key: hasBatteryPlacementRule ? "diag.fix.add_fuse_near_battery" : "diag.fix.add_fuse", vars: { rating: spec.fuse } },
        };
      }
    }
    issues.push({
      code: "unfused-positive-run",
      severity: "critical",
      title: { key: "diag.unfused-positive-run.title", vars: { label } },
      message: { key: "diag.unfused-positive-run.message", vars: { label } },
      nodeIds: [nodeId],
      cableIds,
      fix: { key: "diag.unfused-positive-run.fix" },
      fixAction,
    });
  }
  return issues;
}

/** A load whose positive can't reach any battery+, or negative any battery−. */
export function detectFloatingLoads(
  system: System,
  defLookup: DefLookup,
): Issue[] {
  // Bridge input to output across converters, or a load on an inverter's
  // output reads as floating.
  const converterCategory = new Map<string, ComponentDefinition["category"]>();
  for (const node of system.nodes) {
    if (node.kind !== "component") continue;
    const def = defLookup(node.definitionId, node.customDefinition);
    if (!def || !["charger", "load"].includes(def.category)) continue;
    const hasInput = def.terminals.some((t) => t.direction === "input");
    const hasOutput = def.terminals.some((t) => t.direction === "output");
    if (hasInput && hasOutput) converterCategory.set(node.id, def.category);
  }
  // Supply provenance is per positive/return pair. Bridging every terminal
  // of an input/output component shorts fuse panels and converters across
  // their poles and makes missing returns look healthy.
  const graph = buildGraph(system, defLookup);
  const sources = computeActiveSourceCircuits(system, defLookup);
  const battPos = findTerminals(system, defLookup, isBatteryPositive);
  // AC outputs of a supplied inverter form their own L/N circuit. This also
  // supports imported AC loads whose terminal labels are simply pos/neg.
  for (const node of system.nodes) {
    if (node.kind !== "component") continue;
    const def = defLookup(node.definitionId, node.customDefinition);
    if (!def || !isDcSideSupplied(node, def, "input", sources)) continue;
    for (const line of def.terminals.filter(t => t.direction === "output" && isAcTerminalId(t.id) && /[-_]l$/i.test(t.id))) {
      const neutral = def.terminals.find(t => t.direction === "output" && t.id === line.id.replace(/l$/i, "n"));
      if (!neutral) continue;
      sources.push({ sourceNodeIds: [node.id], positive: reachable(graph, v(node.id, line.id)), negative: reachable(graph, v(node.id, neutral.id)) });
    }
  }
  const posReach = new Set(sources.flatMap(s => [...s.positive]));
  const negReach = new Set(sources.flatMap(s => [...s.negative]));
  // Lazily resolve alternative contact positions only when the default path
  // appears disconnected. Each positive/return proof stays in one state and
  // on the same source; an A-feed plus a B-return is never a complete circuit.
  let alternativeSources: ReturnType<typeof computeActiveSourceCircuits> | undefined;
  const suppliedInAlternative = (nodeId: string, positives: Terminal[], negatives: Terminal[]) => {
    alternativeSources ??= controlDesignSystems(system, defLookup)
      .flatMap(scenario => computeActiveSourceCircuits(scenario, defLookup));
    return alternativeSources.some(source =>
      positives.some(t => source.positive.has(v(nodeId, t.id))) &&
      negatives.some(t => source.negative.has(v(nodeId, t.id))));
  };

  const adjacentConverter = (loadNodeId: string): string | null => {
    for (const c of system.cables) {
      const other =
        c.fromNodeId === loadNodeId
          ? c.toNodeId
          : c.toNodeId === loadNodeId
            ? c.fromNodeId
            : null;
      if (other && converterCategory.has(other)) return other;
    }
    return null;
  };

  const issues: Issue[] = [];
  for (const n of system.nodes) {
    if (n.kind !== "component") continue;
    const def = defLookup(n.definitionId, n.customDefinition);
    if (!def || def.category !== "load") continue;

    const posTerms = def.terminals.filter((t) => isLoadPositive(n, def, t));
    const negTerms = def.terminals.filter((t) => isLoadNegative(n, def, t));
    if (posTerms.length === 0 && negTerms.length === 0) continue;

    const posOk = posTerms.some((t) => posReach.has(v(n.id, t.id)));
    const matchingSources = sources.filter(source => posTerms.some(t => source.positive.has(v(n.id, t.id))));
    const negOk = negTerms.some(t => (posOk ? matchingSources : sources).some(source => source.negative.has(v(n.id, t.id))));
    if ((!posOk || !negOk) && suppliedInAlternative(n.id, posTerms, negTerms)) continue;

    const label = labelOf(system, defLookup, n.id);
    if (battPos.length === 0) {
      // No battery anywhere — the energy pass raises `no-battery`.
      continue;
    }
    if (!posOk && !negOk) {
      const upstreamId = adjacentConverter(n.id);
      if (upstreamId) {
        const upstreamDef = defLookup(
          (system.nodes.find((x) => x.id === upstreamId) as ComponentNode)
            .definitionId,
          (system.nodes.find((x) => x.id === upstreamId) as ComponentNode)
            .customDefinition,
        );
        const upstreamFloating =
          upstreamDef != null &&
          !upstreamDef.terminals.some((t) => posReach.has(v(upstreamId, t.id))) &&
          !upstreamDef.terminals.some((t) => negReach.has(v(upstreamId, t.id)));
        if (upstreamFloating) {
          // An upstream load gets its own `load-floating` card — don't raise both.
          if (converterCategory.get(upstreamId) === "load") continue;
          const upstreamLabel = labelOf(system, defLookup, upstreamId);
          issues.push({
            code: "load-via-floating-converter",
            severity: "warning",
            title: {
              key: "diag.load-via-floating-converter.title",
              vars: { label, upstream: upstreamLabel },
            },
            message: {
              key: "diag.load-via-floating-converter.message",
              vars: { label, upstream: upstreamLabel },
            },
            nodeIds: [n.id, upstreamId],
            fix: {
              key: "diag.load-via-floating-converter.fix",
              vars: { upstream: upstreamLabel },
            },
          });
          continue;
        }
      }
      issues.push({
        code: "load-floating",
        severity: "warning",
        title: { key: "diag.load-floating.title", vars: { label } },
        message: { key: "diag.load-floating.message", vars: { label } },
        nodeIds: [n.id],
        fix: { key: "diag.load-floating.fix" },
      });
    } else if (!posOk) {
      issues.push({
        code: "load-no-positive",
        severity: "warning",
        title: { key: "diag.load-no-positive.title", vars: { label } },
        message: { key: "diag.load-no-positive.message", vars: { label } },
        nodeIds: [n.id],
        fix: { key: "diag.load-no-positive.fix" },
      });
    } else if (!negOk) {
      issues.push({
        code: "load-no-return",
        severity: "warning",
        title: { key: "diag.load-no-return.title", vars: { label } },
        message: { key: "diag.load-no-return.message", vars: { label } },
        nodeIds: [n.id],
        fix: { key: "diag.load-no-return.fix" },
      });
    }
  }
  return issues;
}


/**
 * Battery positives tied together with no switch or isolator between them:
 * differ in chemistry, age or state of charge and one dumps into the other.
 */
export function detectParallelBatteryRisk(
  system: System,
  defLookup: DefLookup,
): Issue[] {
  const graph = buildGraph(system, defLookup);
  // A series stack is one bank here: its cells are not paralleled with each
  // other, and only the posts on its top level meet the shared bus.
  const stacks = computeBatteryStacks(system, defLookup);
  const stackOfVertex = new Map<Vertex, BatteryStack>();
  for (const s of stacks.stacks) for (const vertex of s.topPositives) stackOfVertex.set(vertex, s);
  const battPositives = [...stackOfVertex.keys()];
  if (battPositives.length < 2) return [];

  const groupOf = new Map<Vertex, number>();
  let g = 0;
  for (const seed of battPositives) {
    if (groupOf.has(seed)) continue;
    for (const v of reachable(graph, seed)) {
      if (stackOfVertex.has(v) && !groupOf.has(v)) groupOf.set(v, g);
    }
    g++;
  }
  const groups: BatteryStack[][] = [];
  for (const [vertex, gid] of groupOf) {
    if (!groups[gid]) groups[gid] = [];
    const stack = stackOfVertex.get(vertex)!;
    if (!groups[gid].includes(stack)) groups[gid].push(stack);
  }

  const issues: Issue[] = [];
  for (const group of groups) {
    if (!group) continue;
    // The paralleled units are the top-level batteries of every stack on the
    // rail — two 12 V batteries side by side, or the two top cells of a 2P8S
    // block — never a stack's inner cells.
    const units = group.flatMap((s) =>
      s.levels[s.levels.length - 1].map((nodeId) => ({ nodeId, stack: s })),
    );
    if (units.length < 2) continue;
    const labels = units.map((u) => labelOf(system, defLookup, u.nodeId));
    const nodeIds = group.flatMap((s) => s.batteryNodeIds);

    // Proxy for each unit's distance to the shared bus: the longest cable
    // leaving its posts for something outside its own stack — its + post,
    // and its − post too when the unit is a whole battery rather than the top
    // cell of a string.
    const cableLens = units
      .map((u) => {
        const members = new Set(u.stack.batteryNodeIds);
        const posts = new Set(
          u.stack.levels.length === 1
            ? [...u.stack.topPositives, ...u.stack.bottomNegatives]
            : u.stack.topPositives,
        );
        const candidates = system.cables.filter((c) => {
          const fromPost = c.fromNodeId === u.nodeId && posts.has(v(c.fromNodeId, c.fromTerminalId));
          const toPost = c.toNodeId === u.nodeId && posts.has(v(c.toNodeId, c.toTerminalId));
          if (!fromPost && !toPost) return false;
          const other = fromPost ? c.toNodeId : c.fromNodeId;
          if (members.has(other)) return false;
          // A BMS balance lead or a BMV sense tap says nothing about the
          // unit's distance to the bus.
          const otherNode = findNode(system, other);
          if (otherNode?.kind === "component") {
            const otherDef = defLookup(otherNode.definitionId, otherNode.customDefinition);
            const otherTerm = otherDef?.terminals.find(
              (t) => t.id === (fromPost ? c.toTerminalId : c.fromTerminalId),
            );
            if (
              otherDef?.category === "monitor" &&
              otherTerm &&
              otherTerm.polarity !== "negative" &&
              otherTerm.polarity !== "ground"
            ) {
              return false;
            }
          }
          return true;
        });
        if (candidates.length === 0) return null;
        return Math.max(...candidates.map((c) => c.lengthM));
      })
      .filter((x): x is number => x != null);

    if (cableLens.length >= 2) {
      const min = Math.min(...cableLens);
      const max = Math.max(...cableLens);
      if (max > 0 && (max - min) / max > 0.2) {
        issues.push({
          code: "parallel-battery-imbalance",
          severity: "warning",
          title: { key: "diag.parallel-battery-imbalance.title" },
          message: {
            key: "diag.parallel-battery-imbalance.message",
            vars: {
              labels: labels.join(" / "),
              min: min.toFixed(1),
              max: max.toFixed(1),
            },
          },
          nodeIds,
          fix: { key: "diag.parallel-battery-imbalance.fix" },
        });
      }
    }

    // Battle Born, Victron, Lifeline and Renogy all prohibit paralleling LFP
    // with lead: different resting voltages, so one bank dumps into the other.
    const chemistries = new Set<ComponentDefinition["chemistry"]>();
    for (const id of nodeIds) {
      const n = findNode(system, id);
      if (n?.kind !== "component") continue;
      const def = defLookup(n.definitionId, n.customDefinition);
      if (def?.chemistry) chemistries.add(def.chemistry);
    }
    const hasLfp = chemistries.has("lifepo4");
    const hasLead =
      chemistries.has("agm") ||
      chemistries.has("gel") ||
      chemistries.has("lead-acid");
    if (hasLfp && hasLead) {
      issues.push({
        code: "parallel-battery-chemistry-mismatch",
        severity: "warning",
        title: { key: "diag.parallel-battery-chemistry-mismatch.title" },
        message: {
          key: "diag.parallel-battery-chemistry-mismatch.message",
          vars: { labels: labels.join(" / ") },
        },
        nodeIds,
        fix: { key: "diag.parallel-battery-chemistry-mismatch.fix" },
      });
    }
  }
  return issues;
}

const polarityOf = terminalPolarity;

/** The only parts that get wired in series: cells stacked to 24 V, panels
 *  strung for a higher Voc. Anything else on a +→− cable is a short. */
const canBeSeriesLinked = (
  adef: ComponentDefinition,
  bdef: ComponentDefinition,
): boolean =>
  (adef.category === "battery" && bdef.category === "battery") ||
  (adef.category === "solar" && bdef.category === "solar");

/** A directed battery-source cycle is an opposing loop, not a parallel
 * voltage mismatch. Keep every source in this proof even when a candidate
 * string's own voltage can be resolved after excluding external sources. */
function batteriesInSourceLoops(system: System, defLookup: DefLookup, graph: Graph, ranges: BatteryStack[]): Set<string> {
  const region = batteryConductorRegionIndex(system, defLookup, graph);
  const steps = ranges.filter(range => range.levels.length === 1).map(range => ({
    from: region(range.bottomNegatives[0]), to: region(range.topPositives[0]), nodeIds: range.batteryNodeIds,
  }));
  const above = new Map<number, Set<number>>();
  for (const step of steps) {
    const next = above.get(step.from) ?? new Set<number>();
    next.add(step.to);
    above.set(step.from, next);
  }
  const reachableFrom = new Map<number, Set<number>>();
  const cyclic = new Set<string>();
  for (const step of steps) {
    let reached = reachableFrom.get(step.to);
    if (!reached) {
      reached = new Set([step.to]);
      for (const rail of reached) for (const next of above.get(rail) ?? []) reached.add(next);
      reachableFrom.set(step.to, reached);
    }
    if (reached.has(step.from)) for (const nodeId of step.nodeIds) cyclic.add(nodeId);
  }
  return cyclic;
}

/** Directed source pairs proven by complete external circuits. A loose
 * positive-to-negative jumper still needs the user's series declaration. */
function completeSeriesPairs(system: System, defLookup: DefLookup): Set<string> {
  const pairs = new Set<string>();
  const graph = buildGraph(system, defLookup, { excludeSense: true });
  const potentials = resolveTerminalPotentials(system, defLookup, graph);
  const ports = system.nodes.flatMap(node => {
    if (node.kind !== "component") return [];
    const def = defLookup(node.definitionId, node.customDefinition);
    if (!def) return [];
    const powerDevice = ["load", "charger", "source", "engine", "solar"].includes(def.category);
    const declaredConsumption = def.ampDraw != null && def.ampDraw > 0;
    if (!powerDevice && !declaredConsumption) return [];
    return (["input", "output", "bidi"] as const).flatMap(side => dcPortTerminalGroups(def, side)).map(port => ({
      pos: port.filter(t => t.polarity === "positive").map(t => v(node.id, t.id)),
      neg: port.filter(t => t.polarity === "negative" || t.polarity === "ground").map(t => v(node.id, t.id)),
    }));
  });
  const ranges = computeBatterySourceRanges(system, defLookup, graph);
  let cyclicBatteries: Set<string> | undefined;
  for (const range of ranges) {
    if (range.levels.length < 2) continue;
    const top = range.topPositives[0], bottom = range.bottomNegatives[0];
    let voltage = potentials.voltageBetween(top, bottom);
    if (voltage == null) {
      const cycles = cyclicBatteries ??= batteriesInSourceLoops(system, defLookup, graph, ranges);
      if (range.batteryNodeIds.some(id => cycles.has(id))) continue;
      // An incompatible parallel source can invalidate the global potentials
      // without reversing this string's midpoint. Prove the candidate's own
      // battery rises while retaining ALL physical copper, so shorts and
      // opposing loops through external wiring still invalidate the proof.
      const members = new Set(range.batteryNodeIds);
      const localSources = { ...system, nodes: system.nodes.filter(node => members.has(node.id)) };
      voltage = resolveTerminalPotentials(localSources, defLookup, graph).voltageBetween(top, bottom);
    }
    if (voltage == null || Math.abs(voltage - range.nominalV) > 1e-6) continue;
    const positive = reachable(graph, top), negative = reachable(graph, bottom);
    if (!ports.some(port => port.pos.some(p => positive.has(p)) && port.neg.some(n => negative.has(n)))) continue;
    for (let i = 1; i < range.levels.length; i++) {
      for (const lower of range.levels[i - 1]) for (const upper of range.levels[i]) pairs.add(`${lower}:${upper}`);
    }
  }
  for (const path of computeActiveSolarPaths(system, defLookup)) {
    // Both source terminals are already connected by the candidate cable.
    // Membership in a complete directed generating path excludes dangling,
    // reverse-wired and shorted panels without duplicating the PV traversal.
    for (const lower of path.panelNodeIds) for (const upper of path.panelNodeIds) {
      if (lower !== upper) pairs.add(`${lower}:${upper}`);
    }
  }
  return pairs;
}

/** A positive-to-negative cable is a polarity fault unless explicitly marked
 * as series, or proven to be part of a complete series source circuit. */
export function detectReversedPolarity(
  system: System,
  defLookup: DefLookup,
): Issue[] {
  const issues: Issue[] = [];
  let seriesPairs: Set<string> | undefined;
  for (const c of system.cables) {
    if (c.seriesLink) continue;
    const a = findNode(system, c.fromNodeId);
    const b = findNode(system, c.toNodeId);
    if (!a || !b || a.kind !== "component" || b.kind !== "component") continue;
    const adef = defLookup(a.definitionId, a.customDefinition);
    const bdef = defLookup(b.definitionId, b.customDefinition);
    if (!adef || !bdef) continue;
    const at = adef.terminals.find((t) => t.id === c.fromTerminalId);
    const bt = bdef.terminals.find((t) => t.id === c.toTerminalId);
    if (!at || !bt) continue;
    const ap = polarityOf(at);
    const bp = polarityOf(bt);
    if (
      (ap === "positive" && bp === "negative") ||
      (ap === "negative" && bp === "positive")
    ) {
      if (canBeSeriesLinked(adef, bdef)) {
        seriesPairs ??= completeSeriesPairs(system, defLookup);
        const pair = ap === "positive" ? `${a.id}:${b.id}` : `${b.id}:${a.id}`;
        if (seriesPairs.has(pair)) continue;
      }
      const aLabel = labelOf(system, defLookup, a.id);
      const bLabel = labelOf(system, defLookup, b.id);
      issues.push({
        code: "reversed-polarity",
        severity: "critical",
        title: { key: "diag.reversed-polarity.title", vars: { aLabel, bLabel } },
        message: { key: "diag.reversed-polarity.message" },
        nodeIds: [a.id, b.id],
        cableIds: [c.id],
        fix: { key: "diag.reversed-polarity.fix" },
        ...(canBeSeriesLinked(adef, bdef)
          ? {
              fixAction: {
                kind: "set-cable-series-link" as const,
                cableId: c.id,
                seriesLink: true,
                label: { key: "diag.reversed-polarity.fix-action" },
              },
            }
          : {}),
      });
    }
  }
  return issues;
}

/**
 * A live AC conductor — line or neutral, not PE.
 *
 * `polarity: "neutral"` is not a synonym for AC: catalog defs use it for
 * polarity-agnostic DC posts too. The canonical AC ID prefix determines the
 * domain even when a custom terminal declares positive/negative polarity.
 * `ac-pe` is `"ground"` and deliberately never
 * reaches here — ABYC E-11.5.5.3 requires that bond to DC negative.
 */
function isAcLiveTerminal(t: Terminal): boolean {
  return t.polarity !== "ground" && isAcTerminalId(t.id);
}

function isDcRailTerminal(t: Terminal): boolean {
  return !isAcTerminalId(t.id) && (t.polarity === "positive" || t.polarity === "negative");
}

/**
 * Trace each AC-to-non-AC cable or internal tie through modeled copper to a DC rail.
 * Fuses are conductive; converter power stages are not copper connections.
 * PE/ground paths are excluded: source-specific neutral/earth bonding belongs
 * to ac-protection.ts, not an AC-to-DC power-connection finding.
 */
export function detectAcOntoDcRail(
  system: System,
  defLookup: DefLookup,
): Issue[] {
  const terminals = new Map<Vertex, { node: ComponentNode; terminal: Terminal }>();
  for (const node of system.nodes) {
    if (node.kind !== "component") continue;
    const def = defLookup(node.definitionId, node.customDefinition);
    if (!def) continue;
    for (const terminal of effectiveTerminals(def, node)) {
      terminals.set(v(node.id, terminal.id), { node, terminal });
    }
  }
  // Keep physical fused links and sense/shared posts, with no synthetic
  // converter bridges. The raw graph also has dangling endpoints: validate
  // every visited vertex so two nonexistent posts cannot create a path.
  const graph = buildGraph(system, defLookup);
  const boundaries: Array<{ ac: { node: ComponentNode; terminal: Terminal }; start: Vertex; cableId?: string }> = [];
  for (const cable of system.cables) {
    const from = v(cable.fromNodeId, cable.fromTerminalId);
    const to = v(cable.toNodeId, cable.toTerminalId);
    const a = terminals.get(from);
    const b = terminals.get(to);
    if (!a || !b) continue;
    const aAc = isAcLiveTerminal(a.terminal);
    const bAc = isAcLiveTerminal(b.terminal);
    if (aAc === bAc) continue;
    const ac = aAc ? a : b;
    if ((aAc ? b : a).terminal.polarity === "ground") continue;
    boundaries.push({ ac, start: aAc ? to : from, cableId: cable.id });
  }
  // Imported custom switching maps can explicitly tie an AC port to a DC
  // post inside the part, even when its incoming cable is correctly AC→AC.
  for (const [vertex, ac] of terminals) {
    if (!isAcLiveTerminal(ac.terminal)) continue;
    const starts = new Set<Vertex>();
    for (const edge of graph.adj.get(vertex) ?? []) {
      const endpoint = terminals.get(edge.vertex);
      if (edge.via.kind !== "intra" || !endpoint || endpoint.terminal.polarity === "ground" || isAcLiveTerminal(endpoint.terminal) || starts.has(edge.vertex)) continue;
      starts.add(edge.vertex);
      boundaries.push({ ac, start: edge.vertex });
    }
  }
  const issues: Issue[] = [];
  for (const { ac, start, cableId } of boundaries) {
    const previous = new Map<Vertex, { vertex: Vertex; cableId?: string }>();
    const seen = new Set<Vertex>([start]);
    const queue = [start];
    let target: Vertex | undefined;
    for (let i = 0; i < queue.length; i++) {
      const current = queue[i];
      if (isDcRailTerminal(terminals.get(current)!.terminal)) {
        target = current;
        break;
      }
      for (const edge of graph.adj.get(current) ?? []) {
        const endpoint = terminals.get(edge.vertex);
        // A live AC terminal starts its own boundary cable. Do not backtrack
        // through the inlet or repeat the same fault along an AC panel.
        // A source may intentionally bond N→PE; do not continue through PE
        // to DC negative and mislabel that bond as a DC power connection.
        if (!endpoint || seen.has(edge.vertex) || endpoint.terminal.polarity === "ground" || isAcLiveTerminal(endpoint.terminal)) continue;
        seen.add(edge.vertex);
        previous.set(edge.vertex, {
          vertex: current,
          ...(edge.via.kind === "cable" ? { cableId: edge.via.cableId } : {}),
        });
        queue.push(edge.vertex);
      }
    }
    if (!target) continue;
    const dc = terminals.get(target)!;
    const vertices = [target];
    const cableIds: string[] = [];
    for (let cursor = target; cursor !== start;) {
      const step = previous.get(cursor)!;
      if (step.cableId) cableIds.push(step.cableId);
      vertices.push(step.vertex);
      cursor = step.vertex;
    }
    cableIds.reverse();
    vertices.reverse();
    issues.push({
      code: "ac-onto-dc-rail",
      severity: "critical",
      title: {
        key: "diag.ac-onto-dc-rail.title",
        vars: {
          acLabel: labelOf(system, defLookup, ac.node.id),
          acTerminal: ac.terminal.label,
          dcLabel: labelOf(system, defLookup, dc.node.id),
          dcTerminal: dc.terminal.label,
        },
      },
      message: { key: "diag.ac-onto-dc-rail.message" },
      nodeIds: [...new Set([ac.node.id, ...vertices.map(vertex => terminals.get(vertex)!.node.id)])],
      cableIds: cableId ? [cableId, ...cableIds] : cableIds,
      fix: { key: "diag.ac-onto-dc-rail.fix" },
    });
  }
  return issues;
}

/**
 * `seriesLink` only makes physical sense between two batteries or two solar
 * panels. Anywhere else a +→− jumper is still a short.
 */
export function detectSuspiciousSeriesLink(
  system: System,
  defLookup: DefLookup,
): Issue[] {
  const issues: Issue[] = [];
  for (const c of system.cables) {
    if (!c.seriesLink) continue;
    const a = findNode(system, c.fromNodeId);
    const b = findNode(system, c.toNodeId);
    if (!a || !b || a.kind !== "component" || b.kind !== "component") continue;
    const adef = defLookup(a.definitionId, a.customDefinition);
    const bdef = defLookup(b.definitionId, b.customDefinition);
    if (!adef || !bdef) continue;
    const bothBatteries =
      adef.category === "battery" && bdef.category === "battery";
    const bothSolar =
      adef.category === "solar" && bdef.category === "solar";
    if (bothBatteries || bothSolar) continue;
    const aLabel = labelOf(system, defLookup, a.id);
    const bLabel = labelOf(system, defLookup, b.id);
    issues.push({
      code: "suspicious-series-link",
      severity: "warning",
      title: { key: "diag.suspicious-series-link.title", vars: { aLabel, bLabel } },
      message: { key: "diag.suspicious-series-link.message" },
      nodeIds: [a.id, b.id],
      cableIds: [c.id],
      fix: { key: "diag.suspicious-series-link.fix" },
      fixAction: {
        kind: "set-cable-series-link",
        cableId: c.id,
        seriesLink: false,
        label: { key: "diag.suspicious-series-link.fix-action" },
      },
    });
  }
  return issues;
}

/**
 * A fuse above the cable's wire ampacity melts the wire before it opens. Two
 * codes by where the rating comes from: the cable's own override or an
 * inline holder's element (on either of its posts) → `fuse-exceeds-wire`,
 * the fix being a smaller fuse or a fatter wire; an inherited battery+ MRBF →
 * `inline-fuse-needed`, where the fix is a second inline fuse, never a fatter
 * wire to host a 300 A MRBF under a 25 A load.
 */
export function detectFuseExceedsWire(
  system: System,
  defLookup: DefLookup,
  autoSizeTargetPct: number = DEFAULT_AUTO_SIZE_VDROP_TARGET,
): Issue[] {
  const issues: Issue[] = [];
  const nodesById = indexNodesById(system.nodes);
  const seen = new Set<string>();
  const specOf = makeDiagnosticCableSpecGetter(system, defLookup, autoSizeTargetPct);

  for (const c of system.cables) {
    const effective = getEffectiveCableFuse(c, nodesById, defLookup);
    const rating = effective.ratingA;
    if (rating == null || rating <= 0) continue;
    const spec = specOf(c);
    // An AC run is the inlet breaker's to protect; a rating left on it by an
    // earlier connect flow is not a fuse the engine judges.
    if (spec.isAc || spec.sizingVerificationRequired) continue;
    // ABYC E-11.10.1.1.1 EXCEPTION 1 excepts cranking conductors from
    // overcurrent protection. Skipped only once sized for the crank: a wire
    // that can't carry it still overheats before the fuse opens.
    if (spec.isCranking && spec.continuous > 0 && !isOverloaded(spec)) continue;
    if (rating <= spec.wire.ampacity + 1e-9) continue;
    if (seen.has(c.id)) continue;
    seen.add(c.id);
    // A holder's rating also reports source "battery", but the holder is the
    // inline fuse — asking for another one is not the fix.
    if (effective.source.rating === "battery" && effective.holderRatingA == null) {
      issues.push({
        code: "inline-fuse-needed",
        severity: "critical",
        title: {
          key: "diag.inline-fuse-needed.title",
          vars: { rating, mm2: spec.wire.mm2 },
        },
        message: {
          key: "diag.inline-fuse-needed.message",
          vars: { rating, ampacity: spec.wire.ampacity },
        },
        nodeIds: [c.fromNodeId, c.toNodeId],
        cableIds: [c.id],
        fix: { key: "diag.inline-fuse-needed.fix" },
      });
    } else {
      issues.push({
        code: "fuse-exceeds-wire",
        severity: "critical",
        title: {
          key: "diag.fuse-exceeds-wire.title",
          vars: { rating, mm2: spec.wire.mm2 },
        },
        message: {
          key: "diag.fuse-exceeds-wire.message",
          vars: { rating, ampacity: spec.wire.ampacity },
        },
        nodeIds: [c.fromNodeId, c.toNodeId],
        cableIds: [c.id],
        fix: { key: "diag.fuse-exceeds-wire.fix" },
      });
    }
  }
  return issues;
}

/**
 * Two cables between the same two terminals — they overlap visually, so nobody
 * sees the duplicate.
 */
export function detectDuplicateCables(system: System): Issue[] {
  const groups = new Map<string, string[]>();
  for (const c of system.cables) {
    const a = `${c.fromNodeId}:${c.fromTerminalId}`;
    const b = `${c.toNodeId}:${c.toTerminalId}`;
    const key = a < b ? `${a}|${b}` : `${b}|${a}`;
    const list = groups.get(key) ?? [];
    list.push(c.id);
    groups.set(key, list);
  }
  const issues: Issue[] = [];
  for (const [, cableIds] of groups) {
    if (cableIds.length < 2) continue;
    // Keep the first: oldest insertion in the array, least likely the stray.
    const dupes = cableIds.slice(1);
    issues.push({
      code: "duplicate-cable",
      severity: "warning",
      title: {
        key: "diag.duplicate-cable.title",
        vars: { count: cableIds.length },
      },
      message: { key: "diag.duplicate-cable.message" },
      cableIds,
      fix: { key: "diag.duplicate-cable.fix" },
      fixAction: {
        kind: "delete-cables",
        cableIds: dupes,
        label:
          dupes.length === 1
            ? { key: "diag.fix.delete_duplicate_one" }
            : {
                key: "diag.fix.delete_duplicate_many",
                vars: { count: dupes.length },
              },
      },
    });
  }
  return issues;
}

/**
 * Two cables between the same node pair run side by side, so lengths differing
 * by over 0.5 m are a typo that skews the V-drop math. Same-terminal-pair
 * cables belong to `detectDuplicateCables` and are skipped.
 */
export function detectMismatchedCableLengths(
  system: System,
  defLookup: DefLookup,
): Issue[] {
  const groups = new Map<string, Array<typeof system.cables[number]>>();
  for (const c of system.cables) {
    if (c.fromNodeId === c.toNodeId) continue;
    const a = c.fromNodeId;
    const b = c.toNodeId;
    const key = a < b ? `${a}|${b}` : `${b}|${a}`;
    const list = groups.get(key) ?? [];
    list.push(c);
    groups.set(key, list);
  }
  const issues: Issue[] = [];
  for (const [key, cables] of groups) {
    if (cables.length < 2) continue;
    const termKey = (c: typeof cables[number]) => {
      const a = `${c.fromNodeId}:${c.fromTerminalId}`;
      const b = `${c.toNodeId}:${c.toTerminalId}`;
      return a < b ? `${a}|${b}` : `${b}|${a}`;
    };
    const firstTerm = termKey(cables[0]);
    if (cables.every((c) => termKey(c) === firstTerm)) continue;

    const lens = cables.map((c) => c.lengthM);
    const min = Math.min(...lens);
    const max = Math.max(...lens);
    if (max - min < 0.5) continue;

    const [aId, bId] = key.split("|");
    const aLabel = labelOf(system, defLookup, aId);
    const bLabel = labelOf(system, defLookup, bId);
    issues.push({
      code: "mismatched-cable-lengths",
      severity: "info",
      title: {
        key: "diag.mismatched-cable-lengths.title",
        vars: { aLabel, bLabel },
      },
      message: {
        key: "diag.mismatched-cable-lengths.message",
        vars: {
          aLabel,
          bLabel,
          min: min.toFixed(1),
          max: max.toFixed(1),
        },
      },
      nodeIds: [aId, bId],
      cableIds: cables.map((c) => c.id),
      fix: { key: "diag.mismatched-cable-lengths.fix" },
    });
  }
  return issues;
}

/**
 * Charger and source positive outputs are *also* battery conductors —
 * ABYC E-11 §11.10.1.1 wants a fuse within 7 in of the battery on every
 * conductor connected to it. `detectUnfusedRuns` only walks from loads.
 */
export function detectUnfusedSourceFeed(
  system: System,
  defLookup: DefLookup,
  autoSizeTargetPct: number = DEFAULT_AUTO_SIZE_VDROP_TARGET,
): Issue[] {
  const profile = resolveDesignProfile(system);
  const hasBatteryPlacementRule = profile.status === "available" && profile.policy.protection.batteryDistanceM != null;
  const fusedGraph = buildGraph(system, defLookup);
  const unfusedGraph = buildGraph(system, defLookup, { excludeFused: true });

  const batteryPositives = findTerminals(system, defLookup, isBatteryPositive);
  if (batteryPositives.length === 0) return [];

  const unfusedReach = new Set<Vertex>();
  for (const seed of batteryPositives) {
    for (const v of reachable(unfusedGraph, seed)) unfusedReach.add(v);
  }
  const anyReach = new Set<Vertex>();
  for (const seed of batteryPositives) {
    for (const v of reachable(fusedGraph, seed)) anyReach.add(v);
  }

  const sourceVerts: Array<{
    vertex: Vertex;
    nodeId: string;
    terminalLabel: string;
    kind: "charger" | "source";
  }> = [];
  for (const n of system.nodes) {
    if (n.kind !== "component") continue;
    const def = defLookup(n.definitionId, n.customDefinition);
    if (!def) continue;
    if (def.category !== "charger" && def.category !== "source") continue;
    for (const t of def.terminals) {
      if (t.direction !== "output") continue;
      if (polarityOf(t) !== "positive") continue;
      sourceVerts.push({
        vertex: v(n.id, t.id),
        nodeId: n.id,
        terminalLabel: typeof t.label === "string" ? t.label.trim() || t.id : t.id,
        kind: def.category as "charger" | "source",
      });
    }
  }

  const issues: Issue[] = [];
  const seen = new Set<string>();
  const specOf = makeDiagnosticCableSpecGetter(system, defLookup, autoSizeTargetPct);

  for (const sv of sourceVerts) {
    if (!anyReach.has(sv.vertex)) continue; // not connected to any battery
    if (!unfusedReach.has(sv.vertex)) continue; // a fuse already protects the path
    if (seen.has(sv.nodeId)) continue;
    seen.add(sv.nodeId);
    const label = labelOf(system, defLookup, sv.nodeId);
    const { prev } = multiSourceBfs(unfusedGraph, batteryPositives);
    const cableIds = pathCables(unfusedGraph, sv.vertex, prev);
    const sourceCableId = cableIds[cableIds.length - 1];
    const sourceCable = sourceCableId ? findCable(system, sourceCableId) : undefined;
    let fixAction: Issue["fixAction"];
    // `spec.fuse === 0` is "nothing to rate against": the issue still stands,
    // but a one-click "add a 0 A fuse" is not a fix.
    if (sourceCable) {
      const spec = specOf(sourceCable);
      if (spec.fuse > 0 && spec.currentBasis !== "provisional-fallback" && !spec.sizingVerificationRequired && !spec.fuseRecommendationUnavailableReason) {
        fixAction = {
          kind: "set-cable-fuse",
          cableId: sourceCable.id,
          rating: spec.fuse,
          label: { key: hasBatteryPlacementRule ? "diag.fix.add_fuse_near_battery" : "diag.fix.add_fuse", vars: { rating: spec.fuse } },
        };
      }
    }
    const code =
      sv.kind === "charger"
        ? "unfused-charger-output"
        : "unfused-source-output";
    const vars: Record<string, string> = sv.kind === "charger" ? { label, terminal: sv.terminalLabel } : { label };
    issues.push({
      code,
      severity: "critical",
      title: { key: `diag.${code}.title`, vars },
      message: { key: hasBatteryPlacementRule ? `diag.${code}.message` : `diag.${code}.message_profile`, vars },
      nodeIds: [sv.nodeId],
      cableIds,
      fix: { key: hasBatteryPlacementRule ? `diag.${code}.fix` : `diag.${code}.fix_profile` },
      fixAction,
    });
  }
  return issues;
}

/**
 * A charger's positive output landing back on the source side of its own
 * input: either both sides reach the same battery, or the output reaches an
 * engine starter lug — electrically the start-battery bus, so it charges the
 * start battery instead of the house bank.
 */
export function detectChargerInputOutputLoop(
  system: System,
  defLookup: DefLookup,
): Issue[] {
  const graph = buildGraph(system, defLookup);

  const battPosByNode = new Map<string, Vertex[]>();
  const enginePosByNode = new Map<string, Vertex[]>();
  for (const n of system.nodes) {
    if (n.kind !== "component") continue;
    const def = defLookup(n.definitionId, n.customDefinition);
    if (!def) continue;
    if (def.category === "battery") {
      const verts = def.terminals
        .filter((t) => isBatteryPositive(n, def, t))
        .map((t) => v(n.id, t.id));
      if (verts.length > 0) battPosByNode.set(n.id, verts);
    } else if (def.category === "engine") {
      // The engine's positive input lug is electrically the cranking bus.
      const verts = def.terminals
        .filter((t) => t.direction === "input" && polarityOf(t) === "positive")
        .map((t) => v(n.id, t.id));
      if (verts.length > 0) enginePosByNode.set(n.id, verts);
    }
  }

  const issues: Issue[] = [];

  for (const n of system.nodes) {
    if (n.kind !== "component") continue;
    const def = defLookup(n.definitionId, n.customDefinition);
    if (!def || def.category !== "charger") continue;

    // A unit marked output-disabled isn't sourcing into its own input bus.
    const disablers = settingsBySemantic(n, def, "disables-output");
    if (disablers.some((d) => d.value === true)) continue;

    const inputsPos: Vertex[] = [];
    const outputsPos: Vertex[] = [];
    for (const t of def.terminals) {
      if (polarityOf(t) !== "positive") continue;
      if (t.direction === "input") inputsPos.push(v(n.id, t.id));
      else if (t.direction === "output") outputsPos.push(v(n.id, t.id));
    }
    if (inputsPos.length === 0 || outputsPos.length === 0) continue;

    const inputReach = new Set<Vertex>();
    for (const s of inputsPos) for (const x of reachable(graph, s)) inputReach.add(x);
    const outputReach = new Set<Vertex>();
    for (const s of outputsPos) for (const x of reachable(graph, s)) outputReach.add(x);

    const chargerLabel = labelOf(system, defLookup, n.id);

    let fired = false;
    for (const [batteryId, verts] of battPosByNode) {
      const inHit = verts.some((v) => inputReach.has(v));
      const outHit = verts.some((v) => outputReach.has(v));
      if (inHit && outHit) {
        const battLabel = labelOf(system, defLookup, batteryId);
        issues.push({
          code: "charger-input-output-loop",
          severity: "critical",
          title: {
            key: "diag.charger-input-output-loop.title",
            vars: { chargerLabel, battLabel },
          },
          message: {
            key: "diag.charger-input-output-loop.message",
            vars: { chargerLabel, battLabel },
          },
          nodeIds: [n.id, batteryId],
          fix: {
            key: "diag.charger-input-output-loop.fix",
            vars: { chargerLabel },
          },
        });
        fired = true;
        break; // one issue per charger
      }
    }
    if (fired) continue;

    // DC-DC-shaped chargers only, or an MPPT wired to the start battery
    // false-positives.
    const hasNonPvInput = def.terminals.some(
      (t) =>
        t.direction === "input" &&
        polarityOf(t) === "positive" &&
        !/pv/i.test(t.id) &&
        !/pv/i.test(t.label),
    );
    if (!hasNonPvInput) continue;
    for (const [engineId, verts] of enginePosByNode) {
      const outHit = verts.some((v) => outputReach.has(v));
      const inHit = verts.some((v) => inputReach.has(v));
      // Requiring the input to reach this engine is what confirms the
      // charger draws from it.
      if (outHit && inHit) {
        const engineLabel = labelOf(system, defLookup, engineId);
        issues.push({
          code: "charger-output-on-cranking-bus",
          severity: "critical",
          title: {
            key: "diag.charger-output-on-cranking-bus.title",
            vars: { chargerLabel },
          },
          message: {
            key: "diag.charger-output-on-cranking-bus.message",
            vars: { chargerLabel, engineLabel },
          },
          nodeIds: [n.id, engineId],
          fix: {
            key: "diag.charger-output-on-cranking-bus.fix",
            vars: { chargerLabel },
          },
        });
        break;
      }
    }
  }

  return issues;
}

/**
 * A fuse-block output slot with no rating picked is an unfused conductor, but
 * `buildGraph` counts every `fused-slot` link as fused whatever its rating, so
 * `detectUnfusedRuns` never sees it.
 */
export function detectFuseBlockSlotUnrated(
  system: System,
  defLookup: DefLookup,
): Issue[] {
  const issues: Issue[] = [];
  const cablesBySlot = new Map<string, typeof system.cables>();
  for (const c of system.cables) {
    for (const endpoint of [
      v(c.fromNodeId, c.fromTerminalId),
      v(c.toNodeId, c.toTerminalId),
    ]) {
      const attached = cablesBySlot.get(endpoint) ?? [];
      attached.push(c);
      cablesBySlot.set(endpoint, attached);
    }
  }
  for (const n of system.nodes) {
    if (n.kind !== "component") continue;
    const def = defLookup(n.definitionId, n.customDefinition);
    if (!def || !isFuseBlockLike(def)) continue;
    const slots = def.terminals.filter((t) => t.direction === "output");
    const offenders: Terminal[] = [];
    // Inline holders keep their rating on the node (`fuseAmp`); the cable
    // only mirrors it.
    const holderRating =
      isFuseHolderVariant(def) && n.fuseAmp != null && n.fuseAmp > 0
        ? n.fuseAmp
        : null;
    for (const slot of slots) {
      const cables = cablesBySlot.get(v(n.id, slot.id));
      if (!cables) continue;
      // Multiple branches may share one physical fuse slot. Its rating can
      // be stored on any attached cable, so an unrated sibling must not hide it.
      const hasRating = cables.some((cable) =>
        (cable.fuseAmpOverride ?? holderRating ?? 0) > 0,
      );
      if (!hasRating) offenders.push(slot);
    }
    if (offenders.length === 0) continue;
    const label = labelOf(system, defLookup, n.id);
    const slotLabels = offenders.map((s) => s.label).join(", ");
    const single = offenders.length === 1;
    issues.push({
      code: "fuse-block-slot-unrated",
      severity: "critical",
      title: {
        key: single
          ? "diag.fuse-block-slot-unrated.title_one"
          : "diag.fuse-block-slot-unrated.title_many",
        vars: { label, slots: slotLabels },
      },
      message: {
        key: single
          ? "diag.fuse-block-slot-unrated.message_one"
          : "diag.fuse-block-slot-unrated.message_many",
        vars: { label, slots: slotLabels },
      },
      nodeIds: [n.id],
      fix: { key: "diag.fuse-block-slot-unrated.fix" },
    });
  }
  return issues;
}

/**
 * The feed between a battery and a fuse block's input is a battery-attached
 * conductor (ABYC E-11 §11.10.1.1) and needs its own fuse: per-slot fusing
 * protects each branch, not the feed. `detectUnfusedRuns` walks from loads and
 * stops at the fused slot, so this seeds from the block's input terminals.
 */
export function detectUnfusedFuseBlockFeed(
  system: System,
  defLookup: DefLookup,
  autoSizeTargetPct: number = DEFAULT_AUTO_SIZE_VDROP_TARGET,
): Issue[] {
  const profile = resolveDesignProfile(system);
  const hasBatteryPlacementRule = profile.status === "available" && profile.policy.protection.batteryDistanceM != null;
  const fusedGraph = buildGraph(system, defLookup);
  const unfusedGraph = buildGraph(system, defLookup, { excludeFused: true });

  const batteryPositives = findTerminals(system, defLookup, isBatteryPositive);
  if (batteryPositives.length === 0) return [];

  const unfusedReach = new Set<Vertex>();
  for (const seed of batteryPositives) {
    for (const v of reachable(unfusedGraph, seed)) unfusedReach.add(v);
  }
  const anyReach = new Set<Vertex>();
  for (const seed of batteryPositives) {
    for (const v of reachable(fusedGraph, seed)) anyReach.add(v);
  }

  const cableTerminals = new Set<string>();
  for (const c of system.cables) {
    cableTerminals.add(v(c.fromNodeId, c.fromTerminalId));
    cableTerminals.add(v(c.toNodeId, c.toTerminalId));
  }
  const inputs: Array<{ vertex: Vertex; nodeId: string }> = [];
  for (const n of system.nodes) {
    if (n.kind !== "component") continue;
    const def = defLookup(n.definitionId, n.customDefinition);
    if (!def?.isFuseBlock) continue;
    // An inline holder is the OCPD for its own feed, and shares `isFuseBlock`
    // with multi-slot blocks — discriminate on variant.
    if (isFuseHolderVariant(def)) continue;
    for (const t of def.terminals) {
      if (t.direction !== "input") continue;
      if (polarityOf(t) !== "positive") continue;
      const vert = v(n.id, t.id);
      if (!cableTerminals.has(vert)) continue;
      inputs.push({ vertex: vert, nodeId: n.id });
    }
  }
  if (inputs.length === 0) return [];

  const issues: Issue[] = [];
  const seen = new Set<string>();
  const specOf = makeDiagnosticCableSpecGetter(system, defLookup, autoSizeTargetPct);
  const { prev } = multiSourceBfs(unfusedGraph, batteryPositives);

  for (const inp of inputs) {
    if (!anyReach.has(inp.vertex)) continue; // not connected to any battery
    if (!unfusedReach.has(inp.vertex)) continue; // a fuse already protects the path
    if (seen.has(inp.nodeId)) continue;
    seen.add(inp.nodeId);
    const label = labelOf(system, defLookup, inp.nodeId);
    const cableIds = pathCables(unfusedGraph, inp.vertex, prev);
    const sourceCableId = cableIds[cableIds.length - 1];
    const sourceCable = sourceCableId ? findCable(system, sourceCableId) : undefined;
    let fixAction: Issue["fixAction"];
    // `spec.fuse === 0` is "nothing to rate against": the issue still stands,
    // but a one-click "add a 0 A fuse" is not a fix.
    if (sourceCable) {
      const spec = specOf(sourceCable);
      if (spec.fuse > 0 && spec.currentBasis !== "provisional-fallback" && !spec.sizingVerificationRequired && !spec.fuseRecommendationUnavailableReason) {
        fixAction = {
          kind: "set-cable-fuse",
          cableId: sourceCable.id,
          rating: spec.fuse,
          label: { key: hasBatteryPlacementRule ? "diag.fix.add_fuse_near_battery" : "diag.fix.add_fuse", vars: { rating: spec.fuse } },
        };
      }
    }
    issues.push({
      code: "unfused-fuse-block-feed",
      severity: "critical",
      title: { key: "diag.unfused-fuse-block-feed.title", vars: { label } },
      message: { key: hasBatteryPlacementRule ? "diag.unfused-fuse-block-feed.message" : "diag.unfused-fuse-block-feed.message_profile", vars: { label } },
      nodeIds: [inp.nodeId],
      cableIds,
      fix: { key: hasBatteryPlacementRule ? "diag.unfused-fuse-block-feed.fix" : "diag.unfused-fuse-block-feed.fix_profile" },
      fixAction,
    });
  }
  return issues;
}

/**
 * A charger side whose positive is wired but whose negative never gets back
 * to a battery −: no current, surfacing only as "the house bank never
 * charges". `detectMissingGround` misses it — BATT− is `direction: "output"`,
 * not `polarity: "ground"`. Checked per side, output and (non-PV, non-AC)
 * input alike, and by reachability rather than "has a cable": three charger
 * negatives all landed on one junction that went nowhere else passed as
 * wired (mareva). The negative may reach *any* battery's − — the returns are
 * one bus — so a DC-DC's IN− on the start battery and OUT− on the house bus
 * both pass.
 */
export function detectChargerReturnFloating(
  system: System,
  defLookup: DefLookup,
): Issue[] {
  const cableTerminals = new Set<string>();
  for (const c of system.cables) {
    cableTerminals.add(v(c.fromNodeId, c.fromTerminalId));
    cableTerminals.add(v(c.toNodeId, c.toTerminalId));
  }
  const graph = buildGraph(system, defLookup);
  const batteryPositives = new Set(findTerminals(system, defLookup, isBatteryPositive));
  const batteryNegatives = new Set(findTerminals(system, defLookup, isBatteryNegative));
  const reaches = (vertex: Vertex, targets: ReadonlySet<Vertex>): boolean => {
    for (const r of reachable(graph, vertex)) if (targets.has(r)) return true;
    return false;
  };

  const issues: Issue[] = [];
  for (const n of system.nodes) {
    if (n.kind !== "component") continue;
    const def = defLookup(n.definitionId, n.customDefinition);
    if (!def || def.category !== "charger") continue;

    const unwired: Terminal[] = [];
    const deadEnd: Terminal[] = [];
    for (const dir of ["output", "input"] as const) {
      // The PV side returns to the panels, not the bank; AC has no DC return.
      const onSide = def.terminals.filter(
        (t) => t.direction === dir && !isPvInputTerminal(t) && !isAcTerminalId(t.id),
      );
      const pos = onSide.filter((t) => polarityOf(t) === "positive");
      const neg = onSide.filter((t) => polarityOf(t) === "negative");
      if (pos.length === 0 || neg.length === 0) continue;
      const wiredPos = pos.filter((t) => cableTerminals.has(v(n.id, t.id)));
      if (wiredPos.length === 0) continue;
      // A declared internal common return can complete the circuit without
      // a second external wire on this alias terminal.
      if (neg.some((t) => reaches(v(n.id, t.id), batteryNegatives))) continue;
      const wiredNeg = neg.filter((t) => cableTerminals.has(v(n.id, t.id)));
      if (wiredNeg.length === 0) {
        unwired.push(...neg);
        continue;
      }
      // A side whose positive reaches no battery either is unfinished rather
      // than missing its return — with no battery drawn at all the energy
      // pass raises `no-battery`, and a DC-DC off a bare alternator has its
      // own check.
      if (!wiredPos.some((t) => reaches(v(n.id, t.id), batteryPositives))) continue;
      if (wiredNeg.some((t) => reaches(v(n.id, t.id), batteryNegatives))) continue;
      deadEnd.push(...wiredNeg);
    }
    if (unwired.length === 0 && deadEnd.length === 0) continue;

    const label = labelOf(system, defLookup, n.id);
    const negLabels = [...unwired, ...deadEnd].map((t) => t.label).join(", ");
    issues.push({
      code: "charger-return-floating",
      severity: "warning",
      title: { key: "diag.charger-return-floating.title", vars: { label } },
      message: {
        key:
          deadEnd.length > 0
            ? "diag.charger-return-floating.message_dead_end"
            : "diag.charger-return-floating.message",
        vars: { label, negLabels },
      },
      nodeIds: [n.id],
      fix: {
        key: "diag.charger-return-floating.fix",
        vars: { label, negLabels },
      },
    });
  }
  return issues;
}

/** Rated standalone DC sources use the same paired-route proof as sizing.
 * A wired negative can still end at an isolated junction or another bank.
 * Ordinary loads/chargers retain their existing connectivity diagnostics. */
export function detectSourceReturnFloating(system: System, defLookup: DefLookup): Issue[] {
  const analysis = computeCableDemandAnalysis(system, defLookup);
  const incomplete = new Set(analysis.unresolvedComponents
    .filter(item => item.reason === "missing-return")
    .map(item => item.nodeId));
  if (!incomplete.size) return [];
  const missingGround = new Set(detectMissingGround(system, defLookup).flatMap(issue => issue.nodeIds ?? []));
  const issues: Issue[] = [];
  for (const node of system.nodes) {
    if (node.kind !== "component" || !incomplete.has(node.id)) continue;
    const def = defLookup(node.definitionId, node.customDefinition);
    if (!def || !isDirectDcSource(def) || !Number.isFinite(def.ampOutput) || (def.ampOutput ?? 0) <= 0) continue;
    const terminals = effectiveTerminals(def, node);
    // A starter input's missing return does not establish an alternator-output
    // defect. Use the same generating-port selection as charging-loop and
    // acceptance checks, including legacy combined starter/alternator studs.
    const generatingPorts = new Set(dcChargingPortGroups({ ...def, terminals })
      .flatMap(group => group.filter(t => t.polarity === "positive"))
      .map(t => `${node.id}:${t.direction ?? "input"}:${t.id}`));
    if (!analysis.unresolvedComponents.some(item => item.nodeId === node.id && item.reason === "missing-return" &&
        item.portId != null && generatingPorts.has(item.portId))) continue;
    // A source whose only return is an ungrounded GND already has the specific
    // missing-ground warning. A separate optional chassis earth must not hide
    // an incomplete explicitly negative output circuit.
    if (missingGround.has(node.id) && !terminals.some(t => t.polarity === "negative")) continue;
    const power = new Set(terminals.filter(t => !isAcTerminalId(t.id) &&
      ["positive", "negative", "ground"].includes(t.polarity)).map(t => t.id));
    const cableIds = system.cables.filter(c =>
      (c.fromNodeId === node.id && power.has(c.fromTerminalId)) ||
      (c.toNodeId === node.id && power.has(c.toTerminalId))).map(c => c.id).sort();
    const label = labelOf(system, defLookup, node.id);
    issues.push({
      code: "source-return-floating", severity: "warning", nodeIds: [node.id], cableIds,
      title: { key: "diag.source-return-floating.title", vars: { label } },
      message: { key: "diag.source-return-floating.message", vars: { label } },
      fix: { key: "diag.source-return-floating.fix" },
    });
  }
  return issues;
}

/**
 * A DC-DC charger's positive input must sit on a bus with a battery on it: the
 * battery clamps the bus and soaks the alternator's load-dump transients.
 * Wire it alternator B+ → start battery → DC-DC input.
 */
export function detectDcDcFedWithoutBatteryBuffer(
  system: System,
  defLookup: DefLookup,
): Issue[] {
  const graph = buildGraph(system, defLookup);

  // An engine's positive input terminal is electrically the alternator B+ bus.
  const altPosByNode = new Map<string, Vertex[]>();
  for (const n of system.nodes) {
    if (n.kind !== "component") continue;
    const def = defLookup(n.definitionId, n.customDefinition);
    if (!def) continue;
    if (def.category === "source") {
      const verts = def.terminals
        .filter((t) => t.direction === "output" && polarityOf(t) === "positive")
        .map((t) => v(n.id, t.id));
      if (verts.length > 0) altPosByNode.set(n.id, verts);
    } else if (def.category === "engine") {
      const verts = def.terminals
        .filter((t) => t.direction === "input" && polarityOf(t) === "positive")
        .map((t) => v(n.id, t.id));
      if (verts.length > 0) altPosByNode.set(n.id, verts);
    }
  }
  if (altPosByNode.size === 0) return [];

  const batteryPosVerts = new Set<Vertex>(
    findTerminals(system, defLookup, isBatteryPositive),
  );

  const issues: Issue[] = [];

  for (const n of system.nodes) {
    if (n.kind !== "component") continue;
    const def = defLookup(n.definitionId, n.customDefinition);
    if (!def || def.category !== "charger") continue;
    const id = def.id.toLowerCase();
    const looksLikeDcDc = id.startsWith("dcdc");
    if (!looksLikeDcDc) continue;

    const inputPosVerts = def.terminals
      .filter((t) => t.direction === "input" && polarityOf(t) === "positive")
      // A DC-DC's PV side is solar, not the alternator feed this protects.
      .filter((t) => !/pv/i.test(t.id) && !/pv/i.test(t.label))
      .map((t) => v(n.id, t.id));
    if (inputPosVerts.length === 0) continue;

    const inputReach = new Set<Vertex>();
    for (const s of inputPosVerts) {
      for (const x of reachable(graph, s)) inputReach.add(x);
    }

    const offendingAltIds: string[] = [];
    for (const [altId, altVerts] of altPosByNode) {
      if (altVerts.some((av) => inputReach.has(av))) {
        offendingAltIds.push(altId);
      }
    }
    if (offendingAltIds.length === 0) continue;

    let hasBatteryBuffer = false;
    for (const bv of batteryPosVerts) {
      if (inputReach.has(bv)) {
        hasBatteryBuffer = true;
        break;
      }
    }
    if (hasBatteryBuffer) continue;

    const chargerLabel = labelOf(system, defLookup, n.id);
    const altLabel = labelOf(system, defLookup, offendingAltIds[0]);
    issues.push({
      code: "dcdc-fed-without-battery-buffer",
      severity: "critical",
      title: {
        key: "diag.dcdc-fed-without-battery-buffer.title",
        vars: { chargerLabel },
      },
      message: {
        key: "diag.dcdc-fed-without-battery-buffer.message",
        vars: { chargerLabel, altLabel },
      },
      nodeIds: [n.id, ...offendingAltIds],
      fix: {
        key: "diag.dcdc-fed-without-battery-buffer.fix",
        vars: { chargerLabel },
      },
    });
  }
  return issues;
}

/**
 * Vmp → Voc when a panel lacks an explicit `pvVoc`. Crystalline silicon runs
 * Voc ≈ 1.20–1.25 × Vmp (NEC §690.7), so 1.22 understates the string rather
 * than overstating it.
 */
const VMP_TO_VOC_FALLBACK = 1.22;

/**
 * Cold-temperature Voc correction. NEC Table 690.7(A): 1.20 for crystalline
 * silicon at −25 °C, 1.25 at −40 °C. The design temperature is not tunable.
 */
export const COLD_TEMP_VOC_MULTIPLIER = 1.2;

const effectivePanelVoc = (def: ComponentDefinition): number | null => {
  if (def.pvVoc != null && def.pvVoc > 0) return def.pvVoc;
  if (def.voltage != null && def.voltage > 0)
    return def.voltage * VMP_TO_VOC_FALLBACK;
  return null;
};

const isPvInputTerminal = (t: Terminal): boolean =>
  t.direction === "input" && (/pv/i.test(t.id) || /pv/i.test(t.label));

/**
 * An MPPT's PV input has a hard open-circuit cap (`pvMaxVoc`) and exceeding it
 * once at cold-morning Voc destroys the controller. Sums Voc along the longest
 * series chain; parallel panels never stack Voc, so their chain is 1.
 */
export function detectMpptOverVoltage(
  system: System,
  defLookup: DefLookup,
): Issue[] {
  const issues: Issue[] = [];
  const graph = buildGraph(system, defLookup);

  type Panel = {
    id: string;
    voc: number;
    posVerts: Set<Vertex>;
    negVerts: Vertex[];
  };
  const panels: Panel[] = [];
  for (const n of system.nodes) {
    if (n.kind !== "component") continue;
    const def = defLookup(n.definitionId, n.customDefinition);
    if (!def || def.category !== "solar") continue;
    const voc = effectivePanelVoc(def);
    if (voc == null) continue;
    const posSet = new Set<Vertex>();
    const negList: Vertex[] = [];
    for (const t of def.terminals) {
      const p = polarityOf(t);
      if (p === "positive") posSet.add(v(n.id, t.id));
      else if (p === "negative") negList.push(v(n.id, t.id));
    }
    if (posSet.size === 0 || negList.length === 0) continue;
    panels.push({ id: n.id, voc, posVerts: posSet, negVerts: negList });
  }
  if (panels.length === 0) return [];

  const posVertToPanel = new Map<Vertex, Panel>();
  for (const p of panels) for (const pv of p.posVerts) posVertToPanel.set(pv, p);
  const negVertToPanel = new Map<Vertex, Panel>();
  for (const p of panels) for (const nv of p.negVerts) negVertToPanel.set(nv, p);

  const walkSegment = (
    seeds: Vertex[],
    terminators: Set<Vertex>,
  ): { headPanels: Panel[]; reachedTerminator: boolean } => {
    const seedSet = new Set(seeds);
    const visited = new Set<Vertex>(seeds);
    const queue: Vertex[] = [...seeds];
    const heads = new Set<Panel>();
    let reachedTerminator = false;
    while (queue.length) {
      const cur = queue.shift()!;
      if (terminators.has(cur)) reachedTerminator = true;
      // Halt at a non-seed panel terminal: the recursion crosses the panel.
      if (!seedSet.has(cur)) {
        const head = posVertToPanel.get(cur);
        if (head) {
          heads.add(head);
          continue;
        }
        // Entering a panel through its return rail is not a series step.
        if (negVertToPanel.get(cur)) continue;
      }
      const adj = graph.adj.get(cur);
      if (!adj) continue;
      for (const { vertex } of adj) {
        if (!visited.has(vertex)) {
          visited.add(vertex);
          queue.push(vertex);
        }
      }
    }
    return { headPanels: [...heads], reachedTerminator };
  };

  for (const n of system.nodes) {
    if (n.kind !== "component") continue;
    const def = defLookup(n.definitionId, n.customDefinition);
    if (!def || def.category !== "charger") continue;
    const pvMax = def.pvMaxVoc;
    if (pvMax == null || pvMax <= 0) continue;

    const pvPosSeeds: Vertex[] = [];
    const pvNegSet = new Set<Vertex>();
    const pvPorts = pvInputTerminalGroups(def);
    // Legacy custom drawings may mix a canonical PV id on one conductor with
    // a PV label on the other. Retain both explicit forms of identification;
    // only the canonical selector may supply an unlabelled common return.
    const pvTerminals = [...new Map([
      ...pvPorts.flat(), ...def.terminals.filter(isPvInputTerminal),
    ].map(t => [t.id, t])).values()];
    for (const t of pvTerminals) {
      const pol = polarityOf(t);
      if (pol === "positive") pvPosSeeds.push(v(n.id, t.id));
      else if (pol === "negative") pvNegSet.add(v(n.id, t.id));
    }
    if (pvPosSeeds.length === 0 || pvNegSet.size === 0) continue;

    let bestVoc = 0;
    let bestChainIds: string[] = [];
    const chain: Panel[] = [];
    const visited = new Set<string>();
    const dfs = (seeds: Vertex[]) => {
      const { headPanels, reachedTerminator } = walkSegment(seeds, pvNegSet);
      if (reachedTerminator && chain.length > 0) {
        const vocSum = chain.reduce((a, p) => a + p.voc, 0);
        if (vocSum > bestVoc) {
          bestVoc = vocSum;
          bestChainIds = chain.map((p) => p.id);
        }
      }
      for (const p of headPanels) {
        if (visited.has(p.id)) continue;
        visited.add(p.id);
        chain.push(p);
        dfs(p.negVerts);
        chain.pop();
        visited.delete(p.id);
      }
    };
    dfs(pvPosSeeds);

    // NEC 690.7(A): compare the cold-corrected Voc, not the STC value.
    const bestVocCold = bestVoc * COLD_TEMP_VOC_MULTIPLIER;
    if (bestVocCold > pvMax + 1e-6) {
      const mpptLabel = labelOf(system, defLookup, n.id);
      issues.push({
        code: "mppt-over-voltage",
        severity: "critical",
        title: {
          key: "diag.mppt-over-voltage.title",
          vars: { label: mpptLabel, max: pvMax },
        },
        message: {
          key: "diag.mppt-over-voltage.message",
          vars: {
            label: mpptLabel,
            voc: bestVoc.toFixed(1),
            vocCold: bestVocCold.toFixed(1),
            max: pvMax,
            panels: bestChainIds.length,
          },
        },
        nodeIds: [n.id, ...bestChainIds],
        fix: { key: "diag.mppt-over-voltage.fix", vars: { max: pvMax } },
      });
    }
  }

  return issues;
}

/**
 * ABYC E-11 §11.10 / ISO 10133: every positive conductor leaving the battery
 * needs its OCPD within 7 in (175 mm) along the conductor. The 40 in
 * dispensation needs sheath or conduit, which nothing here models.
 *
 * Sums cable lengths from each battery+ to the first fused cable, assuming
 * conservatively that the fuse sits at that cable's far end.
 *
 * Cranking conductors are outside the rule (E-11.10.1.1.1 EXCEPTION 1), so a
 * fuse somebody put on one is never "too far", and the walk does not continue
 * through them: the starter is a dead end for this check.
 */
export const FUSE_NEAR_BATTERY_LIMIT_M = 0.175;

export function detectFuseTooFarFromBattery(
  system: System,
  defLookup: DefLookup,
): Issue[] {
  const profile = resolveDesignProfile(system);
  const distanceLimit = profile.policy.protection.batteryDistanceM;
  if (profile.status !== "available" || distanceLimit == null) return [];
  const graph = buildGraph(system, defLookup);
  // One walk per bank, from the posts its feeders leave: an inner series post
  // of a 2P8S block is not a battery conductor's origin, and sixteen cells
  // were raising sixteen copies of one finding.
  const stacks = computeBatteryStacks(system, defLookup).stacks;
  if (stacks.length === 0) return [];
  const cranking = computeCrankingCables(system, defLookup);
  // An AC conductor is never a battery conductor; a PE bonded to DC negative
  // must not carry the walk onto the shore side.
  const acCables = new Set(system.cables.filter(isAcCable).map((c) => c.id));

  // Vertices where the fuse sits inside the device rather than out along the
  // cable, so a cable on one of them has 0 unprotected length at that end.
  const fuseBlockOutputVerts = new Set<Vertex>();
  for (const n of system.nodes) {
    if (n.kind !== "component") continue;
    const def = defLookup(n.definitionId, n.customDefinition);
    if (!def) continue;
    if (isFuseBlockLike(def)) {
      for (const t of def.terminals) {
        if (t.direction === "output") fuseBlockOutputVerts.add(v(n.id, t.id));
      }
    }
    if (n.batteryFuse && def.category === "battery") {
      for (const t of def.terminals) {
        if (t.polarity === "positive") fuseBlockOutputVerts.add(v(n.id, t.id));
      }
    }
  }

  type State = { vertex: Vertex; dist: number; via: string[] };
  const cableById = new Map<string, { lengthM: number }>();
  for (const c of system.cables) cableById.set(c.id, { lengthM: c.lengthM });

  const issues: Issue[] = [];

  for (const stack of stacks) {
    const seeds = stack.topPositives;
    if (seeds.length === 0) continue;
    const batteryNodeId = splitV(seeds[0]).nodeId;
    // Fresh `visited` per bank: a vertex sits at a different distance from
    // each one.
    const visited = new Set<Vertex>(seeds);
    const queue: State[] = seeds.map((vertex) => ({ vertex, dist: 0, via: [] }));
    // One issue per bank; every offending cable still lands in `cableIds`.
    const offendingCables = new Set<string>();
    let worstDistanceM = 0;
    while (queue.length > 0) {
      const cur = queue.shift()!;
      const edges = graph.adj.get(cur.vertex);
      if (!edges) continue;
      for (const { vertex: next, via } of edges) {
        if (via.kind === "intra") {
          if (visited.has(next)) continue;
          visited.add(next);
          queue.push({ vertex: next, dist: cur.dist, via: cur.via });
          continue;
        }
        if (cranking.has(via.cableId) || acCables.has(via.cableId)) continue;
        // A missing cable counts 0, rather than NaN-poisoning the sum.
        const cableLen = cableById.get(via.cableId)?.lengthM ?? 0;
        const newDist = cur.dist + cableLen;
        const newVia = [...cur.via, via.cableId];
        if (via.fuseRating != null) {
          // With the fuse inside a block, the unprotected length is whichever
          // side is not the block; on a generic `fuseAmpOverride` cable we
          // can't know which end holds it, so assume the far one.
          let distanceToOcpd = newDist;
          if (fuseBlockOutputVerts.has(cur.vertex)) {
            distanceToOcpd = cur.dist;
          } else if (fuseBlockOutputVerts.has(next)) {
            distanceToOcpd = cur.dist;
          }
          if (distanceToOcpd > distanceLimit) {
            for (const id of newVia) offendingCables.add(id);
            if (distanceToOcpd > worstDistanceM) worstDistanceM = distanceToOcpd;
          }
          continue; // protected from here on — don't walk past the OCPD
        }
        if (visited.has(next)) continue;
        visited.add(next);
        queue.push({ vertex: next, dist: newDist, via: newVia });
      }
    }
    if (offendingCables.size === 0) continue;
    const distanceMm = Math.round(worstDistanceM * 1000);
    const label = labelOf(system, defLookup, batteryNodeId);
    issues.push({
      code: "fuse-too-far-from-battery",
      severity: "warning",
      title: { key: "diag.fuse-too-far-from-battery.title", vars: { label } },
      message: {
        key: "diag.fuse-too-far-from-battery.message",
        vars: { distanceMm },
      },
      nodeIds: [batteryNodeId, ...stack.batteryNodeIds.filter((id) => id !== batteryNodeId)],
      cableIds: [...offendingCables],
      fix: { key: "diag.fuse-too-far-from-battery.fix" },
    });
  }
  return issues;
}

/**
 * Battery-anchored §11.10 for LFP banks with no terminal fuse: lithium delivers
 * 5–10 kA prospective short-circuit current (Battle Born 100 Ah datasheet:
 * ~7 kA), so nothing on the post means no protection until the far end.
 *
 * Lead chemistries are deliberately not flagged; `detectFuseTooFarFromBattery`
 * covers them. Without `batteryFuse` there is no way to say "fuse at the
 * battery end", so the whole cable length counts as unprotected.
 */
export function detectBatteryMissingTerminalFuse(
  system: System,
  defLookup: DefLookup,
): Issue[] {
  const profile = resolveDesignProfile(system);
  const distanceLimit = profile.policy.protection.batteryDistanceM;
  if (profile.status !== "available" || !profile.policy.protection.batteryTerminalFuseAdvisory || distanceLimit == null) return [];
  const issues: Issue[] = [];
  const nodesById = indexNodesById(system.nodes);
  // Judged per bank at the posts its feeders leave: a series jumper between
  // two cells of a stack, or a parallel tie between two top cells, is not a
  // conductor leaving the bank.
  for (const stack of computeBatteryStacks(system, defLookup).stacks) {
    const members = new Set(stack.batteryNodeIds);
    const unfused: string[] = [];
    for (const vertex of stack.topPositives) {
      const { nodeId, terminalId } = splitV(vertex);
      const node = nodesById[nodeId];
      if (node?.kind !== "component" || node.batteryFuse) continue;
      const def = defLookup(node.definitionId, node.customDefinition);
      if (def?.chemistry !== "lifepo4") continue;
      const exceedsLimit = system.cables.some((c) => {
        const onPost =
          (c.fromNodeId === nodeId && c.fromTerminalId === terminalId) ||
          (c.toNodeId === nodeId && c.toTerminalId === terminalId);
        if (!onPost) return false;
        const other = c.fromNodeId === nodeId ? c.toNodeId : c.fromNodeId;
        if (members.has(other)) return false;
        return c.lengthM > distanceLimit;
      });
      if (exceedsLimit && !unfused.includes(nodeId)) unfused.push(nodeId);
    }
    if (unfused.length === 0) continue;

    const label = labelOf(system, defLookup, unfused[0]);
    issues.push({
      code: "battery-missing-terminal-fuse",
      severity: "warning",
      title: { key: "diag.battery-missing-terminal-fuse.title", vars: { label } },
      message: { key: "diag.battery-missing-terminal-fuse.message", vars: { label } },
      nodeIds: unfused,
      fix: { key: "diag.battery-missing-terminal-fuse.fix" },
    });
  }
  return issues;
}

/**
 * Split-charge distribution parts fed from alternator B+. Their terminal shapes
 * are too heterogeneous to recognise structurally, so add new isolator catalog
 * ids here.
 */
const ALT_CHARGE_SPLIT_DIST_IDS = new Set<string>(["dist-battery-isolator-200"]);

function isAltChargeConsumer(def: ComponentDefinition, term: Terminal): boolean {
  if (term.direction !== "input") return false;
  if (def.category === "charger") return true;
  return ALT_CHARGE_SPLIT_DIST_IDS.has(def.id);
}

/**
 * A charge consumer (charger, diode isolator) on the engine's cranking input
 * rather than its Alt B+ output — it then sizes for cranking current. A plain
 * battery or starter feed on that lug is correct and left alone.
 */
export function detectChargeConsumerOnCrankTerminal(
  system: System,
  defLookup: DefLookup,
): Issue[] {
  const issues: Issue[] = [];
  for (const c of system.cables) {
    if (c.seriesLink) continue;
    for (const eng of [
      { nodeId: c.fromNodeId, termId: c.fromTerminalId, isFrom: true },
      { nodeId: c.toNodeId, termId: c.toTerminalId, isFrom: false },
    ]) {
      const engNode = findNode(system, eng.nodeId);
      if (!engNode || engNode.kind !== "component") continue;
      const engDef = defLookup(engNode.definitionId, engNode.customDefinition);
      if (!engDef || engDef.category !== "engine") continue;
      const engTerm = engDef.terminals.find((t) => t.id === eng.termId);
      // Only the cranking (input) terminal is wrong for a charge consumer.
      if (!engTerm || engTerm.direction !== "input") continue;
      const altTerm = engDef.terminals.find(
        (t) => t.direction === "output" && terminalPolarity(t) === "positive",
      );
      if (!altTerm) continue;
      const farNodeId = eng.isFrom ? c.toNodeId : c.fromNodeId;
      const farTermId = eng.isFrom ? c.toTerminalId : c.fromTerminalId;
      const farNode = findNode(system, farNodeId);
      if (!farNode || farNode.kind !== "component") continue;
      const farDef = defLookup(farNode.definitionId, farNode.customDefinition);
      const farTerm = farDef?.terminals.find((t) => t.id === farTermId);
      if (!farDef || !farTerm || !isAltChargeConsumer(farDef, farTerm)) continue;

      const engLabel = labelOf(system, defLookup, engNode.id);
      const farLabel = labelOf(system, defLookup, farNode.id);
      issues.push({
        code: "charge-on-crank-terminal",
        severity: "warning",
        title: {
          key: "diag.charge-on-crank-terminal.title",
          vars: { engLabel, farLabel },
        },
        message: { key: "diag.charge-on-crank-terminal.message", vars: { farLabel } },
        nodeIds: [engNode.id, farNode.id],
        cableIds: [c.id],
        fix: { key: "diag.charge-on-crank-terminal.fix" },
        fixAction: {
          kind: "re-point-cable",
          cableId: c.id,
          fromNodeId: c.fromNodeId,
          fromTerminalId: eng.isFrom ? altTerm.id : c.fromTerminalId,
          toNodeId: c.toNodeId,
          toTerminalId: eng.isFrom ? c.toTerminalId : altTerm.id,
          label: { key: "diag.charge-on-crank-terminal.fix-action" },
        },
      });
      break;
    }
  }
  return issues;
}

export function runTopologyDiagnostics(
  system: System,
  defLookup: DefLookup,
  // Omit this and every cable in a 5 %-preference system is silently sized
  // against the 3 % table.
  autoSizeTargetPct: number = DEFAULT_AUTO_SIZE_VDROP_TARGET,
): Issue[] {
  const fuseDistanceIssues = detectFuseTooFarFromBattery(system, defLookup);
  const coveredBatteries = new Set(fuseDistanceIssues.flatMap(issue => issue.nodeIds ?? []));
  const terminalFuseIssues = detectBatteryMissingTerminalFuse(system, defLookup).flatMap(issue => {
    const nodeIds = issue.nodeIds?.filter(id => !coveredBatteries.has(id)) ?? [];
    if (!nodeIds.length) return [];
    const vars = { label: labelOf(system, defLookup, nodeIds[0]) };
    return [{ ...issue, nodeIds, title: { ...issue.title, vars }, message: { ...issue.message, vars } }];
  });
  return [
    ...detectUnfusedRuns(system, defLookup, autoSizeTargetPct),
    ...detectChargeConsumerOnCrankTerminal(system, defLookup),
    ...detectReversedPolarity(system, defLookup),
    ...detectAcOntoDcRail(system, defLookup),
    ...detectSuspiciousSeriesLink(system, defLookup),
    ...detectFuseExceedsWire(system, defLookup, autoSizeTargetPct),
    ...detectLynxFuseSumExceedsRating(system, defLookup),
    ...detectPvStringFuseExceedsModule(system, defLookup),
    ...detectFloatingLoads(system, defLookup),
    ...detectMissingGround(system, defLookup),
    ...detectParallelBatteryRisk(system, defLookup),
    ...detectUnsupportedBatteryIsolator(system, defLookup),
    ...detectDuplicateCables(system),
    ...detectMismatchedCableLengths(system, defLookup),
    ...detectChargerInputOutputLoop(system, defLookup),
    ...detectDcDcFedWithoutBatteryBuffer(system, defLookup),
    ...detectUnfusedSourceFeed(system, defLookup, autoSizeTargetPct),
    ...detectUnfusedFuseBlockFeed(system, defLookup, autoSizeTargetPct),
    ...detectFuseBlockSlotUnrated(system, defLookup),
    ...detectChargerReturnFloating(system, defLookup),
    ...detectSourceReturnFloating(system, defLookup),
    ...fuseDistanceIssues,
    ...terminalFuseIssues,
    ...detectFuseAicInadequate(system, defLookup),
    ...detectFuseRatingUnavailable(system, defLookup),
    ...detectUntypedFuseOnHighFaultBank(system, defLookup),
    ...detectBatteryChemistryUnknown(system, defLookup),
    ...detectMpptOverVoltage(system, defLookup),
    ...detectIncompletePvInput(system, defLookup),
  ];
}
